import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import { useDialog } from '@/core/components/base/dialog/use-dialog'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type { OrganizationMember } from './use-organization-member'
import type { Kompetenzprofil, KompetenzprofilAnalysis } from '../models/kompetenzprofil'

export const useMemberKompetenzprofil = () => {
  const route = useRoute()
  const memberId = computed(() => {
    const id = route.params.memberId
    return typeof id === 'string' ? id : undefined
  })

  const memberEndpoint = computed(() => `members/${memberId.value}`)
  const profileEndpoint = computed(() => `members/${memberId.value}/kompetenzprofil`)
  const analysisEndpoint = computed(() => `members/${memberId.value}/kompetenzprofil/analysis`)

  const {
    data: member,
    status: memberStatus,
    load: loadMember,
  } = useGet<OrganizationMember>(memberEndpoint)
  const {
    data: profile,
    status: profileStatus,
    load: loadProfile,
  } = useGet<Kompetenzprofil>(profileEndpoint)
  const {
    data: analysis,
    status: analysisStatus,
    mutate: postAnalysis,
  } = usePost<KompetenzprofilAnalysis, { lessonIds?: string[] }>(analysisEndpoint)

  const selectedLessonIds = ref<string[]>([])
  const analysisMemberId = ref<string>()
  const { openDialog, isOpen, closeDialog } = useDialog()

  const visibleAnalysis = computed(() =>
    analysisMemberId.value === memberId.value ? analysis.value : undefined,
  )

  const load = async () => {
    const id = memberId.value
    if (!id) {
      return
    }
    await Promise.all([loadMember(), loadProfile()])
    if (memberId.value !== id || profileStatus.value !== ApiRequestStatus.Success) {
      return
    }
    selectedLessonIds.value = profile.value?.lessons.map((lesson) => lesson.lessonId) ?? []
    await postAnalysis()
    if (memberId.value !== id || analysisStatus.value !== ApiRequestStatus.Success) {
      return
    }
    analysisMemberId.value = id
  }

  watch(
    memberId,
    () => {
      analysisMemberId.value = undefined
      load()
    },
    { immediate: true },
  )

  const applySelectedLessons = async (lessonIds: string[]) => {
    const id = memberId.value
    closeDialog()
    selectedLessonIds.value = [...lessonIds]
    const allLessonIds = profile.value?.lessons.map((lesson) => lesson.lessonId) ?? []
    const includesAll =
      lessonIds.length === allLessonIds.length &&
      allLessonIds.every((lessonId) => lessonIds.includes(lessonId))
    if (includesAll) {
      await postAnalysis()
    } else {
      await postAnalysis({ lessonIds })
    }
    if (memberId.value !== id) {
      return
    }
  }

  const status = computed(() => {
    if (
      memberStatus.value === ApiRequestStatus.Error ||
      profileStatus.value === ApiRequestStatus.Error
    ) {
      return ApiRequestStatus.Error
    }
    if (
      memberStatus.value === ApiRequestStatus.Success &&
      profileStatus.value === ApiRequestStatus.Success
    ) {
      return ApiRequestStatus.Success
    }
    return ApiRequestStatus.Loading
  })

  return {
    member,
    profile,
    analysis: visibleAnalysis,
    status,
    analysisStatus,
    selectedLessonIds,
    isOpen,
    openDialog,
    closeDialog,
    applySelectedLessons,
  }
}
